// Guided spotlight tutorial RUNNER. Dims the screen and cuts ONE clickable hole at a time (a HUD button,
// a production item, or a map tile), showing an instruction bubble; advances only when the player performs
// that exact action. The runner is scenario-agnostic: the actual lesson script (a Step[] closing over one
// deterministic chapter game, see src/ui/tutorialChapters.ts) is injected by the caller. Chapters guarantee
// teachable moments by injecting state (a trade offer, an enemy to attack, a finished harbor) directly on
// the live game object in the UI layer, so the engine stays pure.

import type { GameState, RegionId } from '../game/types';

export interface TutorialDeps {
  game: GameState;
  camera: { worldToScreen(x: number, y: number): { x: number; y: number }; getView(): { x: number; y: number; scale: number } };
  uiRoot: HTMLElement;
  hexSize: number;
  selectRegion: (id: RegionId | null) => void;
  panTo: (wx: number, wy: number, dur: number) => Promise<void>;
  regionWorld: (id: RegionId) => { x: number; y: number };
  overview: { toggle: () => void };
  menu: { toggle: (tab: 'research' | 'diplomacy') => void; refresh: () => void };
  offerToasts: { show: (ids: number[]) => void };
  refresh: () => void; // re-render the map + refresh every panel (after an injection)
  isBusy: () => boolean; // a move glide / AI replay is playing → hide the overlay so the player can watch
  onFinish: (completed: boolean) => void; // mark onboarded + route onward; completed=true only when the player reached the last step (not Skip/teardown)
}

// 'dom' spotlights a UI element, 'tile' a map hex, 'full' dims everything (welcome/finish), and 'panel' keeps a
// just-opened panel fully VISIBLE but INERT (a transparent full-screen shield swallows its clicks), so the player
// reads it and advances with Next without accidentally tripping its own buttons.
export type Target = { kind: 'dom'; sel: string } | { kind: 'tile'; id: RegionId } | { kind: 'full' } | { kind: 'panel' };

export interface Step {
  text: string;
  ch?: string; // chapter label ("Basics", "War"...) shown as `Basics · 2/4` instead of a scary step count
  target: () => Target;
  onEnter?: () => void;
  onExit?: () => void;
  done?: () => boolean; // polled each frame; omit for a 'full'/manualNext step (advances on the Next button)
  manualNext?: boolean; // show a Next button in the bubble and advance on it (for read-and-continue steps)
}

// Small handle into the live bubble, given to the steps factory so a step can rewrite its own copy
// mid-flight (the "you rejected the trade" adaptive branch) without reaching into the runner's DOM.
export interface TutorialApi {
  setText(html: string): void; // replace the bubble copy for the CURRENT step
  showNext(): void; // turn the current step into a read-and-continue step (reveals the Next button)
}

const elh = (tag: string, cls?: string, text?: string): HTMLElement => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text != null) e.textContent = text;
  return e;
};
const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v));

export function startTutorial(deps: TutorialDeps, makeSteps: (api: TutorialApi) => Step[], opts?: { skipLabel?: string }): { destroy: () => void } {
  // ============================================================ spotlight overlay + rAF loop
  const root = elh('div', 'tut');
  const masks = [elh('div', 'tut__mask'), elh('div', 'tut__mask'), elh('div', 'tut__mask'), elh('div', 'tut__mask')];
  const ring = elh('div', 'tut__ring');
  const bubble = elh('div', 'tut__bubble');
  const stepLabel = elh('div', 'tut__step');
  const textEl = elh('div', 'tut__text');
  const actions = elh('div', 'tut__actions');
  const nextBtn = elh('button', 'tut__next') as HTMLButtonElement;
  const skipBtn = elh('button', 'tut__skip', opts?.skipLabel ?? 'Skip tutorial') as HTMLButtonElement;
  actions.append(nextBtn, skipBtn);
  bubble.append(stepLabel, textEl, actions);
  root.append(...masks, ring, bubble);
  deps.uiRoot.appendChild(root);

  let i = 0;
  let raf = 0;
  let alive = true;

  // the guided script: built by the chapter, closing over its own deterministic game + injections
  const steps: Step[] = makeSteps({
    setText: (html) => {
      textEl.innerHTML = html;
    },
    showNext: () => {
      const s = steps[i];
      if (s) s.manualNext = true;
      nextBtn.style.display = '';
    },
  });

  const setMask = (m: HTMLElement, x: number, y: number, w: number, h: number): void => {
    m.style.left = `${x}px`;
    m.style.top = `${y}px`;
    m.style.width = `${Math.max(0, w)}px`;
    m.style.height = `${Math.max(0, h)}px`;
    m.style.display = w <= 0 || h <= 0 ? 'none' : 'block';
  };

  const rectOf = (t: Target): { x: number; y: number; w: number; h: number } | null => {
    if (t.kind === 'full') return null;
    if (t.kind === 'dom') {
      const node = document.querySelector(t.sel) as HTMLElement | null;
      if (!node) return null;
      const r = node.getBoundingClientRect();
      if (r.width === 0 || r.height === 0) return null;
      const pad = 6;
      return { x: r.left - pad, y: r.top - pad, w: r.width + 2 * pad, h: r.height + 2 * pad };
    }
    if (t.kind !== 'tile') return null; // 'panel' is handled by the caller (transparent shield); never reaches here
    if (t.id < 0) return null; // a degraded seed left this tile unresolved: float the bubble, don't crash
    const w = deps.regionWorld(t.id);
    const s = deps.camera.worldToScreen(w.x, w.y);
    // 0.72: keep the square hole INSIDE the hex. Anything past ~0.75 lets a corner click land nearer a
    // NEIGHBOUR tile's centre, moving the soldier to the wrong tile and soft-locking the step.
    const half = deps.hexSize * deps.camera.getView().scale * 0.72;
    return { x: s.x - half, y: s.y - half, w: 2 * half, h: 2 * half };
  };

  const layout = (rect: { x: number; y: number; w: number; h: number } | null): void => {
    const W = window.innerWidth, Hh = window.innerHeight;
    if (!rect) {
      // full-screen dim: first mask covers everything, others off; ring hidden; bubble centred
      setMask(masks[0]!, 0, 0, W, Hh);
      for (let k = 1; k < 4; k++) setMask(masks[k]!, 0, 0, 0, 0);
      ring.style.display = 'none';
      bubble.style.left = '50%';
      bubble.style.top = '50%';
      bubble.style.transform = 'translate(-50%, -50%)';
      return;
    }
    const x = clamp(rect.x, 0, W), y = clamp(rect.y, 0, Hh);
    const rgt = clamp(rect.x + rect.w, 0, W), bot = clamp(rect.y + rect.h, 0, Hh);
    setMask(masks[0]!, 0, 0, W, y); // top
    setMask(masks[1]!, 0, bot, W, Hh - bot); // bottom
    setMask(masks[2]!, 0, y, x, bot - y); // left
    setMask(masks[3]!, rgt, y, W - rgt, bot - y); // right
    ring.style.display = 'block';
    ring.style.left = `${x}px`;
    ring.style.top = `${y}px`;
    ring.style.width = `${rgt - x}px`;
    ring.style.height = `${bot - y}px`;
    // bubble: below the hole if its MEASURED height fits, else above; clamped horizontally.
    // (A fixed 150px guess let tall bubbles overflow the bottom when the hole sat low on screen.)
    const bw = 320;
    const bx = clamp(x + (rgt - x) / 2 - bw / 2, 10, W - bw - 10);
    const bh = bubble.offsetHeight;
    const below = bot + 14 + bh < Hh - 10;
    bubble.style.transform = 'none';
    bubble.style.left = `${bx}px`;
    bubble.classList.remove('tut__bubble--passthru');
    if (below) {
      bubble.style.top = `${bot + 14}px`;
    } else {
      const ty = Math.max(10, y - 14 - bh);
      bubble.style.top = `${ty}px`;
      // Short viewport: neither above nor below fits, so the clamped bubble may now COVER the hole and
      // swallow the very click the step waits for (the bubble is pointer-events:auto). Slide it beside
      // the hole when there's room; as the last resort let clicks pass through its body (the Next/Skip
      // buttons stay clickable via the --passthru CSS).
      const overlapsHole = ty + bh > y && ty < bot && bx + bw > x && bx < rgt;
      if (overlapsHole) {
        if (rgt + 14 + bw <= W - 10) bubble.style.left = `${rgt + 14}px`;
        else if (x - 14 - bw >= 10) bubble.style.left = `${x - 14 - bw}px`;
        else bubble.classList.add('tut__bubble--passthru');
      }
    }
  };

  const renderStep = (): void => {
    const s = steps[i]!;
    // chapter progress ("War · 2/5") reads far friendlier than a raw "Step 15 of 22"
    if (s.ch) {
      const inCh = steps.filter((x) => x.ch === s.ch);
      stepLabel.textContent = `${s.ch} · ${inCh.indexOf(s) + 1}/${inCh.length}`;
    } else stepLabel.textContent = `Step ${i + 1} of ${steps.length}`;
    textEl.innerHTML = s.text;
    const manual = s.target().kind === 'full' || !!s.manualNext;
    nextBtn.textContent = 'Next ▸';
    nextBtn.style.display = manual ? '' : 'none';
  };
  const enter = (): void => {
    renderStep();
    steps[i]!.onEnter?.();
  };

  const advance = (): void => {
    steps[i]!.onExit?.();
    i += 1;
    if (i >= steps.length) return finish(true); // stepped past the last step = genuine completion
    enter();
  };

  const finish = (completed = false): void => {
    if (!alive) return;
    alive = false;
    cancelAnimationFrame(raf);
    root.remove();
    deps.onFinish(completed);
  };

  nextBtn.addEventListener('click', () => {
    const s = steps[i]!;
    if (s.target().kind === 'full' || s.manualNext) advance();
  });
  // skipping is not completing; and one stray click must not erase the session, so Skip asks once
  let skipArmedUntil = 0;
  skipBtn.addEventListener('click', () => {
    if (Date.now() < skipArmedUntil) return finish(false);
    skipArmedUntil = Date.now() + 3000;
    skipBtn.textContent = 'Leave? Click again';
    window.setTimeout(() => {
      if (Date.now() >= skipArmedUntil) skipBtn.textContent = opts?.skipLabel ?? 'Skip tutorial';
    }, 3100);
  });
  // a click on the dim shroud is blocked by design; make it FEEL redirected (ring pulse), not dead
  for (const m of masks)
    m.addEventListener('click', () => {
      if (m.classList.contains('tut__mask--clear')) return;
      ring.classList.remove('tut__ring--deny');
      void ring.offsetWidth; // restart the one-shot animation
      ring.classList.add('tut__ring--deny');
    });

  let busySince = 0;
  let busyMode = false;
  const tick = (): void => {
    if (!alive) return;
    const busy = deps.isBusy();
    if (busy) {
      if (!busySince) busySince = performance.now();
      // short move glides stay clean (overlay simply hides); a LONG busy stretch is the AI replay, so
      // after 600ms swap to narration: masks off, a slim banner framing what the player is watching
      if (!busyMode && performance.now() - busySince > 600) {
        busyMode = true;
        for (const m of masks) setMask(m, 0, 0, 0, 0);
        ring.style.display = 'none';
        actions.style.display = 'none';
        stepLabel.textContent = 'Between turns';
        textEl.innerHTML = '⏳ Your rivals are taking their turns. Watch what they take.';
        bubble.style.left = '50%';
        bubble.style.top = '14px';
        bubble.style.transform = 'translateX(-50%)';
      }
      root.style.display = busyMode ? 'block' : 'none';
    } else {
      busySince = 0;
      if (busyMode) {
        busyMode = false;
        actions.style.display = '';
        renderStep(); // restore the current step's label, text and buttons
      }
      root.style.display = 'block';
    }
    if (!busy) {
      const s = steps[i]!;
      const t = s.target();
      masks[0]!.classList.toggle('tut__mask--clear', t.kind === 'panel'); // transparent only while shielding a panel
      if (t.kind === 'panel') {
        // keep the just-opened panel fully VISIBLE (no dimming) but INERT: a transparent full-screen shield
        // swallows every click except the bubble's Next/Skip, so reading the panel can't trip its own buttons.
        // The tutorial closes the panel itself on Next (the step's onExit), so no panel control is needed here.
        setMask(masks[0]!, 0, 0, window.innerWidth, window.innerHeight);
        for (let k = 1; k < 4; k++) setMask(masks[k]!, 0, 0, 0, 0);
        ring.style.display = 'none';
        bubble.style.left = '50%';
        bubble.style.transform = 'translateX(-50%)';
        bubble.style.top = `${Math.max(12, window.innerHeight - bubble.offsetHeight - 18)}px`;
      } else {
        let rect = rectOf(t);
        if (rect) {
          // a target outside the viewport would clamp to a zero-size hole and mask EVERYTHING; treat
          // it like a missing target instead (masks drop, bubble floats) so the player is never locked
          const W = window.innerWidth, Hh = window.innerHeight;
          if (rect.x + rect.w < 4 || rect.y + rect.h < 4 || rect.x > W - 4 || rect.y > Hh - 4) rect = null;
        }
        if (t.kind !== 'full' && !rect) {
          // target not on screen yet (a panel hasn't opened): drop the masks so the player can act to reveal it,
          // and just float the instruction bubble centred at the top
          for (const m of masks) setMask(m, 0, 0, 0, 0);
          ring.style.display = 'none';
          bubble.style.left = '50%';
          bubble.style.top = '18px';
          bubble.style.transform = 'translateX(-50%)';
        } else {
          layout(rect);
        }
      }
      if (s.done && s.done()) advance();
    }
    raf = requestAnimationFrame(tick);
  };

  enter();
  raf = requestAnimationFrame(tick);
  return { destroy: () => finish(false) }; // external teardown is not completion
}
